import { jsPDF } from 'jspdf';
import type { RoiInputs, RoiResults } from './roiCalculations';
import { SCENARIO_MULTIPLIERS } from './roiCalculations';

const BRAND_COLOR: [number, number, number] = [79, 70, 229]; // brand.500 indigo
const GRAY_700: [number, number, number] = [55, 65, 81];
const GRAY_500: [number, number, number] = [107, 114, 128];

function fmt(n: number): string {
  // Replace non-breaking/thin spaces from fr-FR locale with regular spaces
  // (jsPDF can't render U+202F / U+00A0 — it spaces out every digit)
  return Math.round(n).toLocaleString('fr-FR').replace(/\s/g, ' ');
}

export function exportRoiPdf(inputs: RoiInputs, results: RoiResults): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = margin;

  // ── Header ──
  doc.setFillColor(...BRAND_COLOR);
  doc.rect(0, 0, pageW, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('MySportSet — Rapport ROI personnalisé', margin, 26);
  y = 50;

  // ── Date + scenario ──
  doc.setTextColor(...GRAY_500);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const scenarioLabels = {
    conservative: 'Prudent (0.7x)',
    moderate: 'Modéré (1.0x)',
    optimistic: 'Optimiste (1.3x)',
  };
  doc.text(
    `Généré le ${new Date().toLocaleDateString('fr-FR')} — Scénario : ${scenarioLabels[inputs.scenario]}`,
    margin,
    y,
  );
  y += 10;

  // ── Your venue ──
  doc.setTextColor(...GRAY_700);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Votre club', margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const venueDetails = [
    `Terrains : ${inputs.courts}`,
    `Tarif horaire : ${inputs.rate} €`,
    `Heures/jour : ${inputs.hoursPerDay}`,
    `No-show : ${inputs.noShowPercent}%`,
    `Membres actifs : ${inputs.activeMembers}`,
    `Revenu/membre/mois : ${inputs.monthlyRevenuePerMember} €`,
    `Réservations tél./jour : ${inputs.phoneBookingsPerDay}`,
    `Churn mensuel : ${inputs.monthlyChurnRate}%`,
  ];
  if (inputs.coaches > 0) {
    venueDetails.push(`Coachs : ${inputs.coaches}`);
    venueDetails.push(`Sessions coaching/sem : ${inputs.coachingSessionsPerWeek}`);
  }

  const col1 = venueDetails.slice(0, Math.ceil(venueDetails.length / 2));
  const col2 = venueDetails.slice(Math.ceil(venueDetails.length / 2));
  col1.forEach((line, i) => {
    doc.text(line, margin, y + i * 5);
  });
  col2.forEach((line, i) => {
    doc.text(line, margin + contentW / 2, y + i * 5);
  });
  y += Math.max(col1.length, col2.length) * 5 + 8;

  // ── Divider ──
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // ── Results ──
  doc.setTextColor(...GRAY_700);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Résultats estimés', margin, y);
  y += 10;

  const resultRows = [
    { label: 'Récupération no-shows', value: `${fmt(results.monthlyNoShow)} €/mois`, annual: `${fmt(results.recoveredRevenue)} €/an` },
    { label: 'Temps admin économisé', value: `${fmt(results.weeklyHoursSaved)}h/semaine`, annual: `${fmt(results.annualHoursSaved)}h/an` },
    { label: 'Revenus liste d\'attente', value: `${fmt(results.monthlyWaitlist)} €/mois`, annual: `${fmt(results.waitlistRevenue)} €/an` },
    { label: 'Fidélisation membres', value: `${fmt(results.monthlyRetention)} €/mois`, annual: `${fmt(results.memberRetention)} €/an` },
    { label: 'Automatisation réservations', value: `${fmt(results.monthlyAutomation)} €/mois`, annual: `${fmt(results.bookingAutomation)} €/an` },
  ];

  doc.setFontSize(10);
  resultRows.forEach((row) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY_500);
    doc.text(row.label, margin, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GRAY_700);
    doc.text(row.value, pageW - margin, y, { align: 'right' });
    // Annual equivalent in smaller text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_500);
    doc.text(`(${row.annual})`, pageW - margin, y + 4, { align: 'right' });
    doc.setFontSize(10);
    y += 9;
  });

  // ── Subtotal + multiplier ──
  y += 2;
  const multiplier = SCENARIO_MULTIPLIERS[inputs.scenario];
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_500);
  doc.setFontSize(9);
  doc.text(
    `Sous-total : ${fmt(results.rawTotal)} € × ${multiplier} (${scenarioLabels[inputs.scenario]})`,
    margin,
    y,
  );
  y += 8;

  // ── Total ──
  doc.setFillColor(...BRAND_COLOR);
  doc.roundedRect(margin, y - 5, contentW, 20, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Impact mensuel total', margin + 6, y + 3);
  doc.text(`${fmt(results.monthlyTotalImpact)} €/mois`, pageW - margin - 6, y + 3, { align: 'right' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `${fmt(results.totalAnnualImpact)} €/an · ${results.revenueUpliftPercent.toFixed(1)}% du CA terrain`,
    pageW - margin - 6, y + 10, { align: 'right' },
  );
  y += 26;

  // ── ROI comparison ──
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY_700);
  doc.text('Retour sur investissement', margin, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_500);
  doc.text(
    `${fmt(results.monthlyTotalImpact)} €/mois de gain estimé vs 99 €/mois d'abonnement — même à la moitié de ces estimations, c'est rentable.`,
    margin,
    y,
  );
  y += 5;
  doc.text(
    'Résultats vérifiés : Fréjus Padel Club (6 terrains) — -85% no-shows, 92% prépaiement, +22% utilisation créneaux.',
    margin,
    y,
  );
  y += 10;

  // ── Methodology note ──
  doc.setTextColor(...GRAY_500);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const disclaimer = `Estimations prudentes basées sur : prépaiement seul (45% réduction no-shows, Mindbody/foreUP), liste d'attente (5% des créneaux vides), 10% réduction churn (ClassPass 2025). Impact estimé : ${results.revenueUpliftPercent.toFixed(1)}% du revenu terrain. Les résultats réels varient selon la taille du club, la localisation, le sport et la démographie des joueurs.`;
  const lines = doc.splitTextToSize(disclaimer, contentW);
  doc.text(lines, margin, y);
  y += lines.length * 4 + 8;

  // ── Sources ──
  doc.setTextColor(...GRAY_700);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Sources', margin, y);
  y += 4;

  const sources = [
    // French sources (venue owners can read these directly)
    { label: 'FFT — Observatoire du Padel 2025 (850 000 joueurs, 4 100 terrains)', url: 'https://www.fft.fr/actualites/padel-expansion-chiffres-2025-pistes-pratiquants-licencies' },
    { label: 'Padel Magazine — Combien rapporte le padel (80 000 €/terrain/an)', url: 'https://padelmagazine.fr/combien-rapporte-reellement-le-padel-en-france/' },
    { label: 'Padel Magazine — Attentes des clubs (disponibilité = critère #1)', url: 'https://padelmagazine.fr/observatoire-du-padel-2025-ce-que-les-pratiquants-attendent-des-clubs-et-la-ou-ca-coince-encore/' },
    // Research sources (methodology backing)
    { label: 'foreUP — No-Show White Paper (10M+ réservations, 500+ clubs)', url: 'https://cdn.prod.website-files.com/67f051f16e93cec5462d99e2/6822ac791af806fa7924ef0b_Whitepaper_NoShows_Q12025.pdf' },
    { label: 'ClassPass — Look Back Report 2025 (rétention, sports de raquette +111%)', url: 'https://classpass.com/blog/2025-classpass-look-back-report/' },
    { label: 'Bain & Company / Harvard — 5% rétention = 25-95% profit', url: 'https://hbswk.hbs.edu/archive/the-economics-of-e-loyalty' },
    { label: 'LTA — Programming & Court Utilisation (occupation, listes d\'attente)', url: 'https://www.lta.org.uk/roles-and-venues/venues/programming-court-utilisation/' },
    { label: 'Playtomic + PwC — Global Padel Report 2025', url: 'https://playtomic.com/global-padel-report' },
  ];

  doc.setFont('helvetica', 'normal');
  sources.forEach((s) => {
    doc.setTextColor(...GRAY_500);
    doc.text(`• ${s.label}`, margin + 2, y);
    doc.setTextColor(...BRAND_COLOR);
    doc.textWithLink(s.url, margin + 4, y + 3, { url: s.url });
    y += 8;
  });
  y += 4;

  // ── Footer ──
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageW - margin, y);
  y += 6;
  doc.setTextColor(...BRAND_COLOR);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('mysportset.com', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_500);
  doc.text('— La plateforme tout-en-un pour les clubs de sport', margin + 35, y);

  doc.save('MySportSet-ROI-Report.pdf');
}
